/**
 * InvoicedBankCheck · iter303ak-fix37 — Invoiced · T20 · Action 10
 * ─────────────────────────────────────────────────────────────────
 * Simple radio-pick prompting operator to check internet banking
 * and record the outcome. Feeds T30 gating (not_found can skip
 * straight to T50).
 */
import React, { useState, useEffect } from 'react';
import { Building2, CheckCircle2, AlertCircle, X } from 'lucide-react';

const OUTCOMES = [
  { key: 'found', label: 'Payment found', desc: 'Full or partial payment landed. Log details at T30.', color: 'emerald' },
  { key: 'partial', label: 'Partial found', desc: 'Some but not all — log what you saw at T30.', color: 'amber' },
  { key: 'not_found', label: 'Not found yet', desc: 'Nothing landed. Move on to Message Client with the gentle nudge letter variant.', color: 'slate' },
];

export default function InvoicedBankCheck({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const [outcome, setOutcome] = useState(value?.outcome || '');
  const [checkedAt, setCheckedAt] = useState(value?.checked_at || new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState(value?.note || '');
  useEffect(() => { if (onChange) onChange({ outcome, checked_at: checkedAt, note }); }, [outcome, checkedAt, note]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="wf-invoiced-bank-check">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <Building2 className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Bank Payment Check</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Log in to internet banking and check for incoming payments matching the invoice reference. Pick the outcome — this drives the T50 auto-route.'}
            </div>
          </div>
        </div>
      </div>

      {/* iter303ak-fix38 · Quick-launch to Westpac Online Banking. */}
      <a href="https://www.westpac.co.nz/olb/"
        target="_blank" rel="noopener noreferrer"
        data-testid="wf-invoiced-bank-check-westpac-link"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-all">
        <Building2 size={14} /> Open Westpac Online Banking
        <span className="text-xs opacity-80">↗ new tab</span>
      </a>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {OUTCOMES.map(opt => (
          <button key={opt.key} onClick={() => setOutcome(opt.key)}
            data-testid={`wf-invoiced-bank-check-${opt.key}`}
            className={`text-left p-4 border-2 rounded-xl transition-all
              ${outcome === opt.key
                ? `border-${opt.color}-500 bg-${opt.color}-50 shadow`
                : 'border-slate-200 bg-white hover:border-slate-300'}`}>
            <div className={`text-sm font-semibold mb-1 ${outcome === opt.key ? `text-${opt.color}-900` : 'text-slate-800'}`}>
              {opt.key === 'found' ? <CheckCircle2 size={14} className="inline mr-1" /> :
                opt.key === 'partial' ? <AlertCircle size={14} className="inline mr-1" /> :
                <X size={14} className="inline mr-1" />}
              {opt.label}
            </div>
            <div className="text-xs text-slate-600">{opt.desc}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Checked date</label>
          <input type="date" value={checkedAt} onChange={(e) => setCheckedAt(e.target.value)}
            data-testid="wf-invoiced-bank-check-date"
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Note (optional)</label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Called client to confirm they're sending Monday"
            data-testid="wf-invoiced-bank-check-note"
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!outcome}
            data-testid="wf-invoiced-bank-check-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
